import React, { useMemo, useState, useEffect, useRef, useCallback } from "react";
import dayjs from "dayjs";
import {
  Box,
  Typography,
  IconButton,
  InputAdornment,
  Button,
  TextField,
  Autocomplete,
  Divider,
  Checkbox,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  useTheme,
  useMediaQuery,
  Paper,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import CallRoundedIcon from "@mui/icons-material/CallRounded";
import SmsRoundedIcon from "@mui/icons-material/SmsRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import PersonSearchRoundedIcon from "@mui/icons-material/PersonSearchRounded";
import PersonPinCircleRoundedIcon from "@mui/icons-material/PersonPinCircleRounded";
import ContentCopyRoundedIcon from "@mui/icons-material/ContentCopyRounded";
import api from "services/api";
import { useStateContext } from "contexts/ContextProvider";
import { OVERLAY_NAMES } from "constants/Overlays";
import { usePageTitle } from "hooks/usePageTitle";
import { getMileageRateForDate } from "helpers/mileageRateHelper";
import { normalizePhoneNumber } from "helpers/ServiceHelpers";
import { HtmlTooltip } from "components/HeatMap";
import Overlay from "components/Overlay/Overlay";
import {
  EMPTY_LINGUIST_CLIENT_STATS,
  buildLinguistTooltipContent,
  formatLastAssignmentAgo,
} from "helpers/linguistTooltipHelper";

const EMPTY_DISTANCE = { distanceMiles: null };
const EMPTY_LANGUAGE_CLIENT_STATS = { requestCount: 0 };

const InterpretationPreAssign = () => {
  usePageTitle("Interpretation Pre-Assign");

  const theme = useTheme();
  const isMobile = !useMediaQuery("(min-width:600px)");

  const { overlayData, setOverlayData, setOverlayName, showSnackbar } =
    useStateContext();

  const clients = useMemo(() => overlayData?.Clients || [], [overlayData?.Clients]);
  const languages = useMemo(() => overlayData?.Languages || [], [overlayData?.Languages]);
  const linguists = useMemo(() => overlayData?.Linguists || [], [overlayData?.Linguists]);

  const [client, setClient] = useState(overlayData?.PrefilledData?.Client || null);
  const [language, setLanguage] = useState(
    overlayData?.PrefilledData?.Language || null,
  );
  const [address, setAddress] = useState(overlayData?.PrefilledData?.Address || "");

  const [linguistDistancesById, setLinguistDistancesById] = useState({});
  const [isCalculatingDistances, setIsCalculatingDistances] = useState(false);
  const [distanceCalculationFailed, setDistanceCalculationFailed] = useState(false);
  const [reachedOutById, setReachedOutById] = useState({});
  const [languageClientStatsById, setLanguageClientStatsById] = useState({});
  const [isSearchConfirmed, setIsSearchConfirmed] = useState(false);
  const [distanceSource, setDistanceSource] = useState("");
  const [linguistClientStatsById, setLinguistClientStatsById] = useState({});
  const lastDistanceFetchKeyRef = useRef("");
  const inFlightDistanceFetchKeyRef = useRef("");
  const showSnackbarRef = useRef(showSnackbar);
  showSnackbarRef.current = showSnackbar;
  const previousInputsRef = useRef({
    clientId: null,
    languageId: null,
    address: "",
    isSearchConfirmed: false,
  });

    const [mileageRates, setMileageRates] = useState([]);

    const getMileageRateForYear = (dateValue) => {
      if (!dateValue) return 0.7;
      return getMileageRateForDate(mileageRates, dateValue, 0.7);
    };

  useEffect(() => {
    api
      .get("/mileageRates")
      .then((res) => {
        console.log("[InterpretationPreAssign] mileageRates response:", res?.data);
        setMileageRates(res.data || []);
      })
      .catch((error) => {
        console.error("[InterpretationPreAssign] Error fetching mileage rates:", error);
      });
  }, []);

  const hasClient = Boolean(client?.id || client?.Account);
  const hasLanguage = Boolean(language?.id || language?.Name);
  const hasAddress = Boolean(String(address || "").trim());
  const hasRequiredFields = hasClient && hasLanguage && hasAddress;
  const isReadyToShowLinguists = hasRequiredFields && isSearchConfirmed;

  const selectedLanguageName = String(language?.Name || "").trim();

  const matchesPreAssignLinguistFilter = useCallback((ling) => {
    if (ling?.Name === "N/A") return true;

    const isCurrent = String(ling?.Status || "").trim() === "Current";
    const languageMatches = Array.isArray(ling?.LinguistLanguages)
      ? ling.LinguistLanguages.some(
          (ll) => String(ll?.Language?.Name || "").trim() === selectedLanguageName,
        )
      : false;

    return isCurrent && languageMatches;
  }, [selectedLanguageName]);

  const filteredLinguistIds = useMemo(
    () =>
      linguists
        .filter(matchesPreAssignLinguistFilter)
        .map((ling) => Number(ling?.id || 0))
        .filter((id) => id > 0),
    [linguists, matchesPreAssignLinguistFilter],
  );

  const filteredLinguistIdsSignature = useMemo(
    () => [...filteredLinguistIds].sort((a, b) => a - b).join(","),
    [filteredLinguistIds],
  );

  const reachedCount = useMemo(
    () => Object.values(reachedOutById).filter(Boolean).length,
    [reachedOutById],
  );

  const hasSelectedClient = Number(client?.id || 0) > 0;

  const resolvedClientAddress = useMemo(() => {
    const main = String(client?.MainAddress || "").trim();
    if (main) return main;

    const line1 = String(client?.AddressLine1 || "").trim();
    const line2 = String(client?.AddressLine2 || "").trim();
    const line3 = String(client?.AddressLine3 || "").trim();
    const joinedLines = [line1, line2, line3].filter(Boolean).join(", ");
    if (joinedLines) return joinedLines;

    return String(client?.Address || "").trim();
  }, [client]);

  const handleUseClientAddress = useCallback(() => {
    if (!resolvedClientAddress) return;
    setAddress(resolvedClientAddress);
  }, [resolvedClientAddress]);

  const languageOptions = useMemo(() => {
    const allLanguages = [...languages];

    if (!hasSelectedClient) {
      return allLanguages;
    }

    return allLanguages.sort((left, right) => {
      const requestDelta =
        (languageClientStatsById[Number(right?.id || 0)]?.requestCount || 0) -
        (languageClientStatsById[Number(left?.id || 0)]?.requestCount || 0);

      if (requestDelta !== 0) return requestDelta;
      return String(left?.Name || "").localeCompare(String(right?.Name || ""));
    });
  }, [hasSelectedClient, languageClientStatsById, languages]);

  const renderLanguageOption = useMemo(
    () => ({ key, ownerState, ...props }, option) => {
      if (option?.inputValue || !option?.id || !hasSelectedClient) {
        return (
          <Box key={key} component="li" {...props}>
            {option?.Name || ""}
          </Box>
        );
      }

      const stats =
        languageClientStatsById[Number(option?.id || 0)] ||
        EMPTY_LANGUAGE_CLIENT_STATS;

      return (
        <Box
          key={key}
          component="li"
          {...props}
          sx={{ display: "flex", alignItems: "center", width: "100%", gap: 1 }}
        >
          <Box component="span" sx={{ minWidth: 0 }}>
            {option?.Name || ""}
          </Box>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ ml: "auto", textAlign: "right", whiteSpace: "nowrap" }}
          >
            {`Interpretations: ${stats.requestCount}`}
          </Typography>
        </Box>
      );
    },
    [hasSelectedClient, languageClientStatsById],
  );

  const filteredLinguists = useMemo(() => {
    if (!selectedLanguageName) return [];

    return linguists
      .filter(matchesPreAssignLinguistFilter)
      .map((ling) => ({
        ...ling,
        DistanceInfo: linguistDistancesById[Number(ling?.id || 0)] || EMPTY_DISTANCE,
      }))
      .sort((a, b) => {
        const aMiles = a?.DistanceInfo?.distanceMiles;
        const bMiles = b?.DistanceInfo?.distanceMiles;
        if (aMiles == null && bMiles == null) {
          return String(a?.Name || "").localeCompare(String(b?.Name || ""));
        }
        if (aMiles == null) return 1;
        if (bMiles == null) return -1;
        return aMiles - bMiles;
      });
  }, [selectedLanguageName, linguists, linguistDistancesById, matchesPreAssignLinguistFilter]);

  const progressMessage = useMemo(() => {
    if (isCalculatingDistances) {
      return `Calculating distances for ${filteredLinguists.length} linguists...`;
    }
    const sourceSuffix = distanceSource ? ` | Source: ${distanceSource.toUpperCase()}` : "";
    return `${filteredLinguists.length} linguists matched | ${reachedCount} reached out${sourceSuffix}`;
  }, [isCalculatingDistances, filteredLinguists.length, reachedCount, distanceSource]);

  useEffect(() => {
    const nextInputs = {
      clientId: Number(client?.id || 0),
      languageId: Number(language?.id || 0),
      address: String(address || "").trim(),
    };

    setIsSearchConfirmed(false);
    setDistanceSource("");
    lastDistanceFetchKeyRef.current = "";
    inFlightDistanceFetchKeyRef.current = "";
    previousInputsRef.current = {
      ...nextInputs,
      isSearchConfirmed: false,
    };
  }, [client?.id, language?.id, address]);

  
  useEffect(() => {
    let isCancelled = false;

    const trimmedAddress = String(address || "").trim();
    const languageName = selectedLanguageName;
    const linguistIds = filteredLinguistIdsSignature
      ? filteredLinguistIdsSignature.split(",").map((id) => Number(id)).filter((id) => id > 0)
      : [];
    const requestKey = `${trimmedAddress.toLowerCase()}|${languageName.toLowerCase()}|${filteredLinguistIdsSignature}`;

    if (!isReadyToShowLinguists || !trimmedAddress || !languageName || !linguistIds.length) {
      setIsCalculatingDistances(false);
      setDistanceCalculationFailed(false);
      setLinguistDistancesById({});
      setDistanceSource("");
      return undefined;
    }

    if (inFlightDistanceFetchKeyRef.current === requestKey) {
      return undefined;
    }

    if (lastDistanceFetchKeyRef.current === requestKey) {
      setIsCalculatingDistances(false);
      return undefined;
    }

    inFlightDistanceFetchKeyRef.current = requestKey;
    setIsCalculatingDistances(true);
    setDistanceCalculationFailed(false);

    const timeoutId = setTimeout(async () => {
      try {
        const response = await api.post("/geocoding/driving-distances", {
          destinationAddress: trimmedAddress,
          linguistIds,
        });
        console.log("[InterpretationPreAssign] driving distances response:", response?.data);

        if (!isCancelled) {
          const responseData = response?.data || {};
          setLinguistDistancesById(responseData);
          const sourceFromBody = String(responseData?.__distanceSource || "").toLowerCase();
          const sourceFromHeader = String(response?.headers?.["x-distance-source"] || "").toLowerCase();
          const resolvedSource = sourceFromBody || sourceFromHeader || "unknown";
          setDistanceSource(resolvedSource);
          setDistanceCalculationFailed(false);
          setIsCalculatingDistances(false);
          lastDistanceFetchKeyRef.current = requestKey;
          inFlightDistanceFetchKeyRef.current = "";
        }
      } catch (error) {
        if (!isCancelled) {
          console.error("[InterpretationPreAssign] driving distance request failed:", error);
          inFlightDistanceFetchKeyRef.current = "";
          setLinguistDistancesById({});
          setDistanceSource("");
          setDistanceCalculationFailed(true);
          setIsCalculatingDistances(false);
          showSnackbarRef.current(
            "There was an issue with the Distance Calculation Server. Please try again later, or manually check the distance using Google Maps.",
            theme,
            "error",
          );
        }
      }
    }, 800);

    return () => {
      isCancelled = true;
      clearTimeout(timeoutId);
      if (inFlightDistanceFetchKeyRef.current === requestKey) {
        inFlightDistanceFetchKeyRef.current = "";
      }
    };
  }, [
    address,
    selectedLanguageName,
    filteredLinguistIdsSignature,
    isReadyToShowLinguists,
    isSearchConfirmed,
    client?.id,
    language?.id,
    theme,
  ]);

  useEffect(() => {
    let isCancelled = false;

    const clientId = Number(client?.id || 0);
    const languageIds = languages
      .map((languageOption) => Number(languageOption?.id || 0))
      .filter((id) => id > 0);

    if (!clientId || !languageIds.length) {
      setLanguageClientStatsById({});
      return undefined;
    }

    const fetchLanguageClientStats = async () => {
      try {
        const response = await api.post("/languages/client-service-request-stats", {
          clientId,
          languageIds,
          serviceCategory: "Interpretation",
        });
        console.log("[InterpretationPreAssign] language client stats response:", response?.data);

        if (!isCancelled) {
          setLanguageClientStatsById(response.data || {});
        }
      } catch (error) {
        if (!isCancelled) {
          setLanguageClientStatsById({});
        }
      }
    };

    fetchLanguageClientStats();

    return () => {
      isCancelled = true;
    };
  }, [client?.id, languages, isSearchConfirmed, language?.id]);

  useEffect(() => {
    let isCancelled = false;

    const clientId = Number(client?.id || 0);
    const linguistIds = filteredLinguistIdsSignature
      ? filteredLinguistIdsSignature.split(",").map((id) => Number(id)).filter((id) => id > 0)
      : [];

    if (!clientId || !linguistIds.length) {
      setLinguistClientStatsById({});
      return undefined;
    }

    const fetchStats = async () => {
      try {
        const response = await api.post("/linguists/client-service-stats", {
          clientId,
          linguistIds,
        });
        console.log("[InterpretationPreAssign] linguist client stats response:", response?.data);
        if (!isCancelled) {
          setLinguistClientStatsById(response.data || {});
        }
      } catch {
        if (!isCancelled) {
          setLinguistClientStatsById({});
        }
      }
    };

    fetchStats();

    return () => {
      isCancelled = true;
    };
  }, [client?.id, filteredLinguistIdsSignature]);

  const toggleReachedOut = (linguistId) => {
    setReachedOutById((prev) => ({
      ...prev,
      [linguistId]: !prev[linguistId],
    }));
  };

  const moveForwardWithLinguist = (selectedLinguist) => {
    const nextOverlayData = {
      ...overlayData,
      Action: OVERLAY_NAMES.INTERPRETATION_ACTION_NEW,
      PrefilledData: {
        ...(overlayData?.PrefilledData || {}),
        Client: client || null,
        Language: language || null,
        Address: String(address || "").trim(),
        Linguist: selectedLinguist || null,
      },
    };

    setOverlayData(nextOverlayData);
    setOverlayName(OVERLAY_NAMES.INTERPRETATION_NEW_EDIT);
  };

  const handleConfirmSearch = () => {
    if (!hasRequiredFields) {
      showSnackbar("Please select Client and Language, and enter Address first.", theme, "warning");
      return;
    }

    setIsSearchConfirmed(true);
  };

  const footerActions = (
    <Box
      sx={{
        display: "flex",
        justifyContent: "end",
        alignItems: "center",
        width: "100%",
        flexDirection: "row",
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "start",
          alignItems: "center",
          width: "50%",
          flexDirection: "row",
        }}
      >
        <Typography
          variant="h5"
          sx={{
            color: !isReadyToShowLinguists
              ? theme.palette.text.secondary
              : isCalculatingDistances
              ? theme.palette.warning[500]
              : filteredLinguists.length > 0
                ? theme.palette.primary.main
                : theme.palette.error[600],
          }}
        >
          {isReadyToShowLinguists
            ? progressMessage
            : "Enter Client, Language, and Address, then click Confirm & Search"}
        </Typography>
      </Box>

      <Box
        sx={{
          display: "flex",
          justifyContent: "end",
          alignItems: "flex-end",
          width: "50%",
          flexDirection: "row",
          gap: isMobile ? 1 : 0,
        }}
      >
        {!isReadyToShowLinguists && isMobile ? (
          <IconButton
            color="primary"
            onClick={handleConfirmSearch}
            disabled={!hasRequiredFields}
            sx={{
              width: "40px",
              height: "40px",
              backgroundColor: theme.palette.primary.main,
              borderRadius: "12px",
              color: "white",
              "&:hover": {
                backgroundColor: theme.palette.primary.buttonHoverSelected,
              },
              "&:disabled": {
                backgroundColor: theme.palette.grey[400],
                color: theme.palette.grey[200],
              },
            }}
          >
            <CheckCircleRoundedIcon />
          </IconButton>
        ) : !isReadyToShowLinguists ? (
          <>
            <Button
              color="primary"
              variant="contained"
              size="large"
              endIcon={<CheckCircleRoundedIcon />}
              onClick={handleConfirmSearch}
              disabled={!hasRequiredFields}
              sx={{ borderRadius: "12px" }}
            >
              Confirm & Search
            </Button>
          </>
        ) : null}
      </Box>
    </Box>
  );

  return (
    <Overlay
      id="interpretation-pre-assign"
      open={true}
      onClose={() => setOverlayName("")}
      title="Pre-Assign Outreach"
      height={isReadyToShowLinguists ? undefined : "fit-content"}
      maxWidthPx={1520}
      scrollable={false}
      footer={footerActions}
    >
        <Box
          component="form"
          sx={{
            display: "flex",
            flexDirection: "column",
            height: isReadyToShowLinguists ? "100%" : "auto",
            minHeight: 0,
            p: "1rem 2.3rem 1rem 2.5rem",
            width: "100%",
            "& .MuiTextField-root": {
              m: 0,
              width: "100%",
            },
          }}
        >
            <Grid container spacing={2} width="100%" sx={{ flexShrink: 0 }}>
            <Grid size={{ xs: 12, md: 6 }}>
              <Autocomplete
                size="small"
                options={clients}
                value={client}
                onChange={(_, v) => setClient(v)}
                getOptionLabel={(opt) => opt?.Account || ""}
                isOptionEqualToValue={(option, value) =>
                  Number(option?.id || 0) === Number(value?.id || 0)
                }
                renderInput={(params) => (
                  <TextField {...params} size="small" label="Client" />
                )}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    py: 0,
                    "&.MuiInputBase-root fieldset": {
                      borderColor: client
                        ? theme.palette.primary.main
                        : theme.palette.error.main,
                    },
                  },
                }}
              />
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <Autocomplete
                size="small"
                options={languageOptions}
                value={language}
                onChange={(_, v) => setLanguage(v)}
                getOptionLabel={(opt) => opt?.Name || ""}
                isOptionEqualToValue={(option, value) =>
                  Number(option?.id || 0) === Number(value?.id || 0)
                }
                renderOption={renderLanguageOption}
                renderInput={(params) => (
                  <TextField {...params} size="small" label="Language" />
                )}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    py: 0,
                    "&.MuiInputBase-root fieldset": {
                      borderColor: language
                        ? theme.palette.primary.main
                        : theme.palette.error.main,
                    },
                  },
                }}
              />
            </Grid>

            <Grid size={{ xs: 12 }}>
              <TextField
                size="small"
                label="Address"
                value={address}
                onChange={(e) => setAddress(e.target.value)}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        size="small"
                        onClick={handleUseClientAddress}
                        disabled={!resolvedClientAddress}
                        title={
                          resolvedClientAddress
                            ? "Use client address"
                            : "No client address available"
                        }
                      >
                        <PersonPinCircleRoundedIcon fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                sx={{
                  "& .MuiInputBase-input": {
                    padding: "8.5px 12px !important",
                  },
                  "& .MuiOutlinedInput-root": {
                    py: 0,
                    "&.MuiInputBase-root fieldset": {
                      borderColor: String(address || "").trim()
                        ? theme.palette.primary.main
                        : theme.palette.error.main,
                    },
                  },
                }}
              />
            </Grid>

            <Grid size={{ xs: 12 }}>
              <Divider />
            </Grid>

            {isReadyToShowLinguists ? (
              <Grid size={{ xs: 12 }}>
                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                  Available Linguists
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Use Call/Text, mark outreach, then Confirm to open the assignment prefilled.
                </Typography>
              </Grid>
            ) : null}

            {isReadyToShowLinguists && !filteredLinguists.length && (
              <Grid size={{ xs: 12 }}>
                <Paper
                  variant="outlined"
                  sx={{
                    borderRadius: "10px",
                    py: 4,
                    textAlign: "center",
                    borderStyle: "dashed",
                  }}
                >
                  <PersonSearchRoundedIcon color="disabled" />
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    Select a language to see matching linguists.
                  </Typography>
                </Paper>
              </Grid>
            )}
            </Grid>

            {isReadyToShowLinguists && filteredLinguists.length > 0 && (
              <Box
                sx={{
                  flex: 1,
                  minHeight: 0,
                  display: "flex",
                  flexDirection: "column",
                  mt: 2,
                }}
              >
                <TableContainer
                  component={Paper}
                  variant="outlined"
                  sx={{
                    flex: 1,
                    minHeight: 0,
                    overflow: "auto",
                    borderRadius: "10px",
                  }}
                >
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 700 }}>Name</TableCell>
                        {!isMobile && <TableCell sx={{ fontWeight: 700 }}>Phone</TableCell>}
                        <TableCell sx={{ fontWeight: 700 }}>Distance</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Mileage Cost</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Assignments</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Last Assignment</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700 }}>Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {filteredLinguists.map((ling) => {
                        const distanceMiles = ling?.DistanceInfo?.distanceMiles ?? null;
                        const linguistAddress = [
                          ling?.AddressRecord?.AddressLine1 || ling?.AddressLine1,
                          ling?.AddressRecord?.AddressLine2 || ling?.AddressLine2,
                          ling?.AddressRecord?.AddressLine3 || ling?.AddressLine3,
                        ]
                          .filter(Boolean)
                          .join(", ") || String(ling?.Address || "").trim();
                        const mapsDirectionsUrl = linguistAddress && address.trim()
                          ? `https://www.google.com/maps/dir/?api=1&origin=${encodeURIComponent(linguistAddress)}&destination=${encodeURIComponent(address.trim())}&travelmode=driving`
                          : "";
                        const distanceText =
                          distanceMiles != null
                            ? `${distanceMiles} mi`
                            : isCalculatingDistances
                              ? "Calculating..."
                              : "—";
                        const phone = String(ling?.PhoneNumber || "").trim();
                        const callPhone = phone.replace(/[^0-9+]/g, "");
                        const stats =
                          linguistClientStatsById[Number(ling?.id || 0)] ||
                          EMPTY_LINGUIST_CLIENT_STATS;

                          const mileageRate = getMileageRateForYear(dayjs().toDate());
                          const mileageCostValue =
                            distanceMiles != null
                              ? (distanceMiles * 2 * mileageRate).toFixed(2)
                              : null;
                          const mileageCostText =
                            mileageCostValue != null
                              ? `~$${mileageCostValue}`
                              : isCalculatingDistances
                                ? "Calculating..."
                                : "—";

                        return (
                          <HtmlTooltip
                            key={ling.id}
                            interactive
                            title={
                              <Box sx={{ p: 0.5 }}>
                                {buildLinguistTooltipContent(
                                  ling,
                                  stats,
                                  distanceMiles,
                                ).map(([label, value, meta]) => (
                                  <Box
                                    key={label}
                                    sx={{
                                      display: "flex",
                                      gap: 1,
                                      py: 0.25,
                                      alignItems: "center",
                                    }}
                                  >
                                    <Typography
                                      variant="caption"
                                      sx={{
                                        fontWeight: 700,
                                        minWidth: "70px",
                                        color: "inherit",
                                      }}
                                    >
                                      {label}:
                                    </Typography>
                                    <Typography
                                      variant="caption"
                                      sx={{ color: "inherit" }}
                                    >
                                      {String(value)}
                                    </Typography>
                                    {meta?.clickable && (
                                      <Box
                                        sx={{
                                          display: "inline-flex",
                                          gap: 0.5,
                                          ml: 0.5,
                                        }}
                                      >
                                        <IconButton
                                          size="small"
                                          component="a"
                                          href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(String(value))}`}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          sx={{
                                            p: 0.25,
                                            color: theme.palette.primary.main,
                                          }}
                                          title="Open in Maps"
                                        >
                                          <PersonPinCircleRoundedIcon fontSize="small" />
                                        </IconButton>
                                        <IconButton
                                          size="small"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            navigator.clipboard
                                              ?.writeText(String(value))
                                              .then(() =>
                                                showSnackbar(
                                                  "Address copied to clipboard",
                                                  theme,
                                                  "success",
                                                ),
                                              )
                                              .catch(() => {});
                                          }}
                                          sx={{
                                            p: 0.25,
                                            color: theme.palette.primary.main,
                                          }}
                                          title="Copy Address"
                                        >
                                          <ContentCopyRoundedIcon fontSize="small" />
                                        </IconButton>
                                      </Box>
                                    )}
                                  </Box>
                                ))}
                              </Box>
                            }
                            placement="right"
                            arrow
                            slotProps={{
                              tooltip: {
                                sx: {
                                  bgcolor: theme.palette.background.paper,
                                  color: theme.palette.text.primary,
                                  boxShadow: theme.shadows[4],
                                  border: `1px solid ${theme.palette.divider}`,
                                  fontSize: "0.75rem",
                                  maxWidth: 360,
                                },
                              },
                              arrow: {
                                sx: {
                                  color: theme.palette.background.paper,
                                },
                              },
                            }}
                          >
                            <TableRow hover>
                              <TableCell sx={{ p: 0, border: 0 }}>
                                <Box
                                  sx={{
                                    px: 2,
                                    py: 1,
                                    minHeight: "48px",
                                    width: { xs: "110px", md: "150px" },
                                    display: "flex",
                                    flexDirection: "column",
                                    justifyContent: "center",
                                  }}
                                >
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      fontWeight: 700,
                                      overflow: "hidden",
                                      textOverflow: "ellipsis",
                                      whiteSpace: "nowrap",
                                    }}
                                    title={ling?.Name || "—"}
                                  >
                                    {ling?.Name || "—"}
                                  </Typography>
                                  {isMobile && phone && (
                                    <Typography
                                      variant="caption"
                                      color="text.secondary"
                                      sx={{
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                        whiteSpace: "nowrap",
                                        display: "block",
                                      }}
                                    >
                                      {normalizePhoneNumber(phone)}
                                    </Typography>
                                  )}
                                </Box>
                              </TableCell>
                              {!isMobile && (
                                <TableCell>
                                  <Typography
                                    variant="body2"
                                    color="text.secondary"
                                    sx={{
                                      overflow: "hidden",
                                      textOverflow: "ellipsis",
                                      whiteSpace: "nowrap",
                                      maxWidth: "130px",
                                    }}
                                  >
                                    {phone ? normalizePhoneNumber(phone) : "—"}
                                  </Typography>
                                </TableCell>
                              )}
                              <TableCell>
                                <Box>
                                  <Typography variant="body2">{distanceText}</Typography>
                                  {distanceCalculationFailed && mapsDirectionsUrl && (
                                    <Button
                                      size="small"
                                      variant="outlined"
                                      component="a"
                                      href={mapsDirectionsUrl}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      startIcon={<PersonPinCircleRoundedIcon />}
                                      sx={{
                                        mt: 0.5,
                                        px: 1,
                                        minHeight: 30,
                                        borderRadius: 1,
                                        fontWeight: 600,
                                        whiteSpace: "nowrap",
                                        "&:hover": { bgcolor: "action.hover" },
                                      }}
                                    >
                                      Manually Check Distance
                                    </Button>
                                  )}
                                </Box>
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2">
                                  {mileageCostText}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2">
                                  {stats.completedAssignments}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2">
                                  {stats.lastAssignmentDate
                                    ? formatLastAssignmentAgo(
                                        stats.lastAssignmentDate,
                                      )
                                    : "—"}
                                </Typography>
                              </TableCell>
                              <TableCell align="right">
                                <Box
                                  sx={{
                                    display: "inline-flex",
                                    alignItems: "center",
                                    gap: 0.25,
                                  }}
                                >
                                  <IconButton
                                    size="small"
                                    component="a"
                                    href={callPhone ? `tel:${callPhone}` : undefined}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    disabled={!callPhone}
                                  >
                                    <CallRoundedIcon fontSize="small" />
                                  </IconButton>
                                  <IconButton
                                    size="small"
                                    component="a"
                                    href={callPhone ? `sms:${callPhone}` : undefined}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    disabled={!callPhone}
                                  >
                                    <SmsRoundedIcon fontSize="small" />
                                  </IconButton>
                                  <Checkbox
                                    size="small"
                                    sx={{ p: 0.5, ml: 1 }}
                                    checked={Boolean(reachedOutById[ling.id])}
                                    onChange={() => toggleReachedOut(ling.id)}
                                  />
                                  <Typography variant="caption">Reached</Typography>
                                  <Button
                                    size="small"
                                    variant="contained"
                                    startIcon={<CheckCircleRoundedIcon />}
                                    onClick={() => moveForwardWithLinguist(ling)}
                                  >
                                    Confirm
                                  </Button>
                                </Box>
                              </TableCell>
                            </TableRow>
                          </HtmlTooltip>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            )}
          </Box>
    </Overlay>
  );
};

export default InterpretationPreAssign;
